import { useMemo } from 'react';
import { usePortfolioBalances } from 'views/Portfolio/components/Summary/hooks/usePortfolioBalances';
import { useReturnValuations } from 'views/Portfolio/components/Summary/hooks/useReturnValuations';
import { PeriodReturn, calculatePeriodReturns } from 'helpers/portfolioReturns';

export interface LabelledPeriodReturn extends PeriodReturn {
  label: string;
  months: number;
}

interface UsePortfolioReturnsResponse {
  returns: LabelledPeriodReturn[];
  isLoading: boolean;
}

/**
 * Portfolio return over 1M/3M/6M/12M, on the same basis as the Performance Story chart.
 *
 * Use this anywhere a performance figure is shown to a client. Do NOT substitute the wine
 * price statistics from usePortalHoldings - those only see wines held today, so they miss
 * anything sold during the period and credit gains made before the client bought in.
 *
 * Both underlying queries are shared by react-query cache key, so calling this alongside the
 * Portfolio page costs no extra requests.
 */
export const usePortfolioReturns = (portfolioId?: number | string | null): UsePortfolioReturnsResponse => {
  const { portfolioBalances, loading: loadingBalances } = usePortfolioBalances();

  const accountHolderId = useMemo(
    () => portfolioBalances.find((p) => p.accountHolderId)?.accountHolderId ?? null,
    [portfolioBalances],
  );

  // -1 is the app's "all portfolios" sentinel; useReturnValuations expects null for that,
  // otherwise it would filter the API down to a portfolio literally called "-1".
  const normalisedPortfolioId =
    portfolioId === null || portfolioId === undefined || String(portfolioId) === '-1' ? null : String(portfolioId);

  const { returnValuations, loading: loadingValuations } = useReturnValuations(normalisedPortfolioId, accountHolderId);

  const returns = useMemo(() => calculatePeriodReturns(returnValuations), [returnValuations]);

  // useReturnValuations disables its query without an accountHolderId, and a disabled query
  // reports isLoading forever. Once balances are in and there is still no id, there is nothing
  // coming - report settled so callers show "n/a" rather than a skeleton that never resolves.
  const isLoading = loadingBalances || (accountHolderId !== null && loadingValuations);

  return { returns, isLoading };
};
